'use client';

import { Separator } from '@mokoboko/components';

import { detailPath } from '@/helpers/offering';
import useAdminUsers from '@/state/admin-users';
import { AppearanceSettings } from '@/state/appearance-settings';
import useClassGroups from '@/state/class-groups';
import useFaqs from '@/state/faqs';
import { FaqSection } from './faq-section';
import { MeetYourInstructorsSection } from './meet-your-instructors-section';
import { OurLocationSection } from './our-location-section';
import { PickYourClassSection } from './pick-your-class-section';

// A pack or membership page ends in sections that each fill in from a read of
// their own: the classes, the instructors, then the location and the FAQ.
// Drawn as each read landed, a later section opened above an earlier one and
// pushed it down, so the page holds the run until the reads it shows are in.
// These are the reads the sections make themselves, so nothing inside fetches
// on mount and flips this back.
export const OfferingDetailSections = ({
  settings,
  classIds,
  productType,
  slug,
}: {
  settings: AppearanceSettings['class_pack' | 'subscription'];
  classIds: string[];
  productType: 'class-pack' | 'subscription';
  slug: string;
}) => {
  const isSubscription = productType === 'subscription';
  const showClasses = settings.show_formats_section;
  const showInstructors = settings.show_instructors_section;
  // The instructors section reads the classes too, for its bio panel.
  const needsClasses = showClasses || showInstructors;
  const { isLoading: isClassesLoading } = useClassGroups({
    include_classes: true,
    enabled: needsClasses,
  });
  const { isLoading: isInstructorsLoading } = useAdminUsers({
    role: 'INSTRUCTOR',
    enabled: showInstructors,
  });
  const { data: faqs, isLoading: isFaqsLoading } = useFaqs({
    entityType: isSubscription ? 'SUBSCRIPTION' : 'CLASS_PACK',
    slug,
  });

  if (
    (needsClasses && isClassesLoading) ||
    (showInstructors && isInstructorsLoading) ||
    isFaqsLoading
  ) {
    return null;
  }

  return (
    <>
      {showClasses && (
        <PickYourClassSection classIds={classIds} productType={productType} />
      )}
      {showInstructors && (
        <MeetYourInstructorsSection
          sitePath={detailPath(isSubscription ? 'subscription' : 'pack', slug)}
        />
      )}
      {settings.show_location_section && <OurLocationSection />}

      {faqs.length > 0 && (
        <>
          <Separator />
          <FaqSection faqs={faqs} className="max-sm:pt-0" />
        </>
      )}
    </>
  );
};
